import { useTranslation } from 'react-i18next'
import { Pencil } from 'lucide-react'
import { Avatar } from './avatar'
import { cn } from '@/lib/utils'
import type { PresenceFrame, PresencePerson } from '@/lib/realtime/presenceStore'
import { displayPersonName } from '@/lib/personName'

interface PresenceStackProps {
  frame: PresenceFrame | null
  /** Faces drawn before "+N". */
  max?: number
  size?: 'sm' | 'md'
  className?: string
}

const SIZE = { sm: 'h-7 w-7 text-xs', md: 'h-8 w-8 text-sm' } as const

const labelOf = (person: PresencePerson, t: (key: string, opts?: Record<string, unknown>) => string) => {
  const name = displayPersonName(t, person.name)
  if (person.editing) return t('presence.editingTooltip', { name })
  return person.state === 'idle' ? t('presence.idleTooltip', { name }) : name
}

/**
 * Who else is on this screen (area presence): up to `max` stacked faces and
 * "+N" for the rest of `count`. Idle people (connected, quiet for 30 s or
 * more) are drawn faded; an editor carries a pencil. Nobody else: nothing.
 */
export const PresenceStack = ({ frame, max = 4, size = 'md', className }: PresenceStackProps) => {
  const { t } = useTranslation('common')
  if (!frame || frame.count <= 0 || !frame.members.length) return null
  const shown = frame.members.slice(0, max)
  const rest = Math.max(0, frame.count - shown.length)
  const summary = t('presence.summary', { count: frame.count })
  return (
    <div className={cn('flex items-center', className)} role="group" aria-label={summary} data-testid="presence-stack">
      <ul className="flex -space-x-2">
        {shown.map((person, index) => {
          const label = labelOf(person, t)
          return (
            <li
              key={person.memberId}
              className="group relative"
              style={{ zIndex: shown.length - index }}
              data-presence-state={person.state}
              data-presence-editing={person.editing ? 'true' : undefined}
            >
              <span tabIndex={0} aria-label={label} className="block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                <Avatar
                  src={person.avatarUrl}
                  alt=""
                  fallback={person.name || '?'}
                  className={cn(SIZE[size], 'border-2 border-white transition-opacity', person.state === 'idle' && 'opacity-40 grayscale')}
                />
                {person.editing && (
                  <span className={cn('absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-white bg-primary-600 text-white', person.state === 'idle' && 'opacity-50')}>
                    <Pencil className="h-2 w-2" aria-hidden />
                  </span>
                )}
              </span>
              <span
                role="tooltip"
                className="pointer-events-none absolute right-0 top-full z-50 mt-1.5 hidden whitespace-nowrap rounded-md theme-light bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block group-focus-within:block"
              >
                {label}
              </span>
            </li>
          )
        })}
        {rest > 0 && (
          <li
            className={cn(SIZE[size], 'relative flex items-center justify-center rounded-full border-2 border-white bg-gray-100 font-medium text-gray-600')}
            style={{ zIndex: 0 }}
            title={summary}
          >
            +{rest}
          </li>
        )}
      </ul>
    </div>
  )
}
